import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Flag, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PickSelect } from "@/components/apply/Field";
import { StatusBadge } from "@/components/StatusBadge";
import { PageTitle } from "@/pages/admin/AdminLayout";
import { api, downloadFile, fmtDate, formatErr } from "@/lib/api";
import { CATEGORIES } from "@/data/options";

const STATUSES = ["New", "ID Under Review", "Interview Scheduled", "Interviewed", "Approved", "Declined", "Cash Payment Received", "Active Member"];
const TIERS = ["bronze", "silver", "gold", "platinum"];
const EMPTY = { status: "", tier: "", country: "", category: "", date_from: "", date_to: "", q: "" };
const ALL = "__all";
const opts = (list) => [{ value: ALL, label: "All" }, ...list.map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))];

const Stats = ({ stats }) => (
  <div className="grid gap-3 grid-cols-2 md:grid-cols-4 xl:grid-cols-8 mb-8">
    {STATUSES.map((s) => (
      <div key={s} className="rounded-2xl border border-border bg-card p-4" data-testid={`stat-${s.toLowerCase().replace(/\s/g, "-")}`}>
        <p className="font-display text-2xl font-extrabold">{stats?.by_status?.[s] ?? 0}</p>
        <p className="text-xs text-muted-foreground font-semibold">{s}</p>
      </div>
    ))}
  </div>
);

export default function Applicants() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState({ items: [], countries: [] });
  const [stats, setStats] = useState(null);
  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v === ALL ? "" : v }));

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [a, s] = await Promise.all([api.get("/admin/applications", { params }), api.get("/admin/stats")]);
      setData(a.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-applicants-page">
      <PageTitle title="Applicants" sub={`${stats?.total ?? 0} total applications`}>
        <button onClick={() => downloadFile("/admin/export/applicants", "applicants.csv")} data-testid="export-applicants-button" className="inline-flex h-10 items-center gap-2 px-4 rounded-full border border-border font-semibold text-sm hover:bg-secondary transition-colors"><Download className="h-4 w-4" />Export applicants</button>
        <button onClick={() => downloadFile("/admin/export/members", "members.csv")} data-testid="export-members-button" className="inline-flex h-10 items-center gap-2 px-4 rounded-full bg-primary text-primary-foreground font-semibold text-sm"><Download className="h-4 w-4" />Export members</button>
      </PageTitle>
      <Stats stats={stats} />
      <div className="rounded-2xl border border-border bg-card p-4 mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <div className="relative xl:col-span-2">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search name, email, business" value={filters.q} onChange={(e) => setF("q")(e.target.value)} className="h-11 pl-9 rounded-xl" data-testid="filter-search-input" />
        </div>
        <PickSelect value={filters.status || ALL} onChange={setF("status")} options={opts(STATUSES)} placeholder="Status" testId="filter-status-select" />
        <PickSelect value={filters.tier || ALL} onChange={setF("tier")} options={opts(TIERS)} placeholder="Tier" testId="filter-tier-select" />
        <PickSelect value={filters.country || ALL} onChange={setF("country")} options={opts(data.countries)} placeholder="Country" testId="filter-country-select" />
        <PickSelect value={filters.category || ALL} onChange={setF("category")} options={opts(CATEGORIES)} placeholder="Category" testId="filter-category-select" />
        <div className="flex gap-2 items-center xl:col-span-1">
          <Input type="date" value={filters.date_from} onChange={(e) => setF("date_from")(e.target.value)} className="h-11 rounded-xl" data-testid="filter-date-from" />
        </div>
        <div className="flex gap-2 items-center">
          <Input type="date" value={filters.date_to} onChange={(e) => setF("date_to")(e.target.value)} className="h-11 rounded-xl" data-testid="filter-date-to" />
          <button onClick={() => setFilters(EMPTY)} className="h-11 w-11 shrink-0 grid place-items-center rounded-xl border border-border hover:bg-secondary" data-testid="filter-clear-button" aria-label="Clear filters"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <Table data-testid="applicants-table">
          <TableHeader>
            <TableRow>
              <TableHead>Applicant</TableHead><TableHead>Country</TableHead><TableHead>Category</TableHead>
              <TableHead>Tier</TableHead><TableHead>Status</TableHead><TableHead>Score</TableHead><TableHead>Submitted</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground" data-testid="applicants-empty">No applicants match these filters.</TableCell></TableRow>
            )}
            {data.items.map((a) => (
              <TableRow key={a.id} className="hover:bg-secondary/60" data-testid={`applicant-row-${a.id}`}>
                <TableCell>
                  <Link to={`/admin/applicants/${a.id}`} className="font-bold hover:text-primary inline-flex items-center gap-2" data-testid={`applicant-link-${a.id}`}>
                    {a.red_flag && <Flag className="h-4 w-4 text-red-500 fill-red-500" />}{a.contact.full_name}
                  </Link>
                  <p className="text-xs text-muted-foreground">{a.contact.email}</p>
                </TableCell>
                <TableCell>{a.contact.country}</TableCell>
                <TableCell>{a.profile.category}</TableCell>
                <TableCell className="capitalize font-semibold">{a.tier}</TableCell>
                <TableCell><StatusBadge status={a.status} /></TableCell>
                <TableCell>{a.vetting_score ?? "-"}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{fmtDate(a.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
